import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'

export interface PlayerSeasonStatRow {
  sport_id?: number
  generic_sport_id?: string | null
  stats?: Array<{
    value?: string
    abbr?: string
    type?: { abbr?: string }
  }>
  team?: unknown
  season?: { name?: string; starts_at?: string }
  [key: string]: unknown
}

export interface GetPlayerSeasonStatsArgs {
  playerId: string
  sportId?: number
  genericSportId?: string | null
  goalie: boolean
}

/**
 * Fetches a single player's per-season stats for one sport variant
 * (skater or goalie) and filters the response to rows that match the
 * requested sport (and generic sport, when provided). Mirrors the filtering
 * the Profile stats screen used to do inline.
 *
 * `sportId` is effectively required: with it undefined the sport filter drops
 * every row. Callers gate the request on a selected sport (the consuming hook
 * sets `enabled` only when `sportId != null`), so this is a low-level fetch and
 * the guard lives one layer up.
 */
export const getPlayerSeasonStats = async ({
  playerId,
  sportId,
  genericSportId = null,
  goalie,
}: GetPlayerSeasonStatsArgs): Promise<PlayerSeasonStatRow[]> => {
  const response = await req(
    ENDPOINTS.PLAYERS.GET_PLAYER_PROFILE_STATS(playerId),
    {
      query: {
        goalie: goalie ? 1 : 0,
        sort: 'desc',
        // API default; applies to all sports currently supported.
        sortBy: 'points',
        sport_id: sportId,
        // Pass undefined (not null) when there's no generic sport so the query
        // builder omits the param entirely — `null` would serialize to the
        // literal `generic_sport_id=null`.
        generic_sport_id: genericSportId ?? undefined,
      },
    }
  )

  if (!Array.isArray(response)) {
    return []
  }

  const bySport = response
    .filter(
      (item): item is PlayerSeasonStatRow =>
        typeof item === 'object' && item !== null
    )
    .filter((item) => item.sport_id === sportId)

  if (genericSportId) {
    return bySport.filter((item) => item.generic_sport_id === genericSportId)
  }

  return bySport
}
